import { api } from '@/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ProfileDetailsFormValues } from '../ProfileDetailsForm'

export type UserProfile = {
  ageBracket: string
  gender: string
  raceEthnicity: string
  countryOfOriginCode: string
  countryOfResidenceCode: string
  languages?: {
    languageCode: string
    level: string
    isMain: boolean
  }[]
}

export function userProfileToFormValues(profile: UserProfile): ProfileDetailsFormValues {
  return {
    ageBracket: profile.ageBracket,
    gender: profile.gender,
    raceEthnicity: profile.raceEthnicity,
    countryOfOriginCode: profile.countryOfOriginCode,
    countryOfResidenceCode: profile.countryOfResidenceCode,
    nativeLanguage: profile.languages?.find((s) => s.isMain)?.languageCode ?? '',
    secondaryLanguages:
      profile.languages
        ?.filter((s) => !s.isMain)
        .map((s) => ({
          language: s.languageCode,
          level: s.level
        })) || []
  }
}

export function formValuesToUserProfile(data: ProfileDetailsFormValues): UserProfile {
  return {
    ageBracket: data.ageBracket,
    gender: data.gender,
    raceEthnicity: data.raceEthnicity,
    countryOfOriginCode: data.countryOfOriginCode,
    countryOfResidenceCode: data.countryOfResidenceCode,
    languages: [
      ...(data.secondaryLanguages?.map((s) => ({
        languageCode: s.language,
        level: s.level,
        isMain: false
      })) || []),
      {
        languageCode: data.nativeLanguage,
        level: 'Native',
        isMain: true
      }
    ]
  }
}

export const useProfile = () => {
  return useQuery({
    queryKey: ['USER_PROFILE'],
    queryFn: () => api.get<UserProfile>('user/profile')
  })
}

export const useUpdateProfile = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UserProfile) => api.put<UserProfile>('user/profile', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['USER_PROFILE'] })
    }
  })
}
